<template>
  <div id="app">
    <w-app>
      <!--Header Code-->
      <header v-show="isAuthenticated">
        <!--Logo-->
        <div class="center-screen">
          <img src="./assets/Station49new.png" height="150" style="border-radius: var(--radius-md);">
        </div>
      </header>

      <!--Center Section-->
      <main class="grow" :class="{ 'auth-failure-main': !isAuthenticated }">

        <!--Keypad code-entry screen (primary sign-in)-->
        <div v-show="keypadVisible && !isAuthenticated" class="keypad-screen">
          <div class="center-screen">
            <img src="./assets/Station49new.png" height="110" style="border-radius: var(--radius-md);">
          </div>
          <div class="center-screen"><h1>Gate Access</h1></div>
          <div class="center-screen"><p class="keypad-subtitle">Enter your gate code</p></div>
          <div class="center-screen"><p class="keypad-hint">You must allow your location to open the gate.</p></div>

          <div class="center-screen">
            <div class="code-dots">
              <span v-for="i in 4" :key="i" class="code-dot" :class="{ filled: codeDigits.length >= i }"></span>
            </div>
          </div>

          <div v-if="codeError" class="center-screen">
            <p class="keypad-error">{{ codeError }}</p>
          </div>

          <div v-if="pendingSubmit && !codeSubmitting" class="center-screen">
            <p class="keypad-subtitle">Complete the check below to open ↓</p>
          </div>

          <div class="center-screen">
            <div class="keypad-grid">
              <w-button v-for="n in [1,2,3,4,5,6,7,8,9]" :key="n" class="keypad-key" @click="pressDigit(n)"
                :disabled="codeSubmitting" text xl shadow>{{ n }}</w-button>
              <w-button class="keypad-key keypad-key-muted" @click="clearCode()" :disabled="codeSubmitting" text sm shadow>Clear</w-button>
              <w-button class="keypad-key" @click="pressDigit(0)" :disabled="codeSubmitting" text xl shadow>0</w-button>
              <w-button class="keypad-key keypad-key-muted" @click="pressBackspace()" :disabled="codeSubmitting" text xl shadow>⌫</w-button>
            </div>
          </div>

          <!-- Cloudflare Turnstile — rendered only when VUE_APP_TURNSTILE_SITE_KEY is configured -->
          <div class="center-screen" style="margin-top: var(--space-md);">
            <div ref="turnstile" class="cf-turnstile-container"></div>
          </div>

          <div v-if="codeSubmitting" class="center-screen">
            <p class="keypad-subtitle">Checking…</p>
          </div>
        </div>

        <!--Authentication Failure Screen (legacy hard-failure fallback)-->
        <div v-show="page1" class="auth-failure-page">
          Authentication failed
        </div>

        <!--Gate Control Screen-->
        <div v-show="page2">
          <div class="center-screen">
            <h1>Gate Control</h1>
          </div>
          <div class="center-screen">
            <p>{{ name }}</p>
          </div>
          <br>
          <div class="center-screen">
            <h2>Gate Status: {{ gateDisplayStatus }}</h2>
          </div>

          <!-- Mode indicator -->
          <div v-if="gateMode === 'default_open'" class="center-screen">
            <p class="gate-mode-text">Mode: Hold Open (Manager Only)</p>
          </div>

          <!-- Duration when gate is open -->
          <div v-if="gateStatus === 'Open' && gateOpenDuration && !gateOffline" class="center-screen">
            <p class="gate-status-text">Open for {{ gateOpenDuration }}</p>
          </div>

          <!-- Auto-close countdown. The deadline is the server's; this only renders it. -->
          <div v-if="autoCloseCountdown !== null" class="center-screen">
            <p class="gate-status-text">Auto-closing in {{ autoCloseCountdown }}s</p>
          </div>


          <!-- Main gate button -->
          <br>
          <div class="center-screen">
            <w-button class="ma1" :bg-color="gateOffline ? 'grey' : (transitionState ? 'warning' : (gateStatus === 'Open' ? 'error' : 'success'))" width="220" height="80" shadow
              text xl @click="gateOffline ? fetchGateStatus() : toggleGate()"
              :disabled="gateActionDisabled">
              {{ gateActionLabel }}
            </w-button>
          </div>

          <!-- Non-manager lockout message -->
          <div v-if="!isManager && gateMode === 'default_open'" class="center-screen">
            <p class="gate-lockout-text">Gate is being held open. Contact a manager to operate.</p>
          </div>

          <!-- Manager buttons -->
          <div v-if="isManager" class="center-screen" style="margin-top: var(--space-lg);">
            <div class="button-group">
              <w-button class="ma1" bg-color="info" width="120" height="45" shadow text md @click="showCreateUser">
                Users
              </w-button>
              <w-button class="ma1" bg-color="info" width="120" height="45" shadow text md @click="showGroups">
                Groups
              </w-button>
              <w-button class="ma1" bg-color="secondary" width="120" height="45" shadow text md @click="showHistory">
                History
              </w-button>
              <w-button class="ma1" bg-color="primary" width="120" height="45" shadow text md @click="showGateCodes">
                Codes
              </w-button>
            </div>
          </div>

          <div v-if="isManager" class="center-screen" style="margin-top: var(--space-sm);">
            <div class="button-group">
              <w-button class="ma1" :bg-color="gateMode === 'default_open' ? 'warning' : 'secondary'" width="150" height="40" shadow text sm
                @click="setGateMode(gateMode === 'default_open' ? 'default_closed' : 'default_open')"
                :disabled="isLoading">
                {{ gateMode === 'default_open' ? 'Release Hold' : 'Hold Open' }}
              </w-button>
            </div>
          </div>
        </div>

        <!--Gate Codes Screen (manager)-->
        <div v-show="page8">
          <div class="center-screen"><h1>Gate Codes</h1></div>
          <div class="center-screen">
            <w-button class="ma1" bg-color="secondary" width="120" height="40" shadow text sm @click="pageSwitch(2)">
              ← Back
            </w-button>
          </div>

          <div v-if="gateCodesError" class="center-screen">
            <p class="keypad-error">{{ gateCodesError }}</p>
          </div>

          <div class="codes-panel">
            <!-- Manager — same PIN as station49aws, deliberately not settable here -->
            <div class="code-card">
              <div class="code-card-head">
                <span class="code-role">Manager code</span>
                <span class="code-value code-value-hidden">••••</span>
              </div>
              <p class="code-hint">
                The same PIN as the station49aws manager login — change it there, not here.
                Full panel access, and the only code that works away from the gate.
                <span v-if="gateCodes.manager_updated_at"><br>Last changed {{ formatCodeDate(gateCodes.manager_updated_at) }}</span>
              </p>
            </div>

            <!-- Regulars — stored encrypted, so set-only -->
            <div class="code-card">
              <div class="code-card-head">
                <span class="code-role">Regulars code</span>
                <span class="code-value code-value-hidden">••••</span>
              </div>
              <p class="code-hint">
                For your regulars. Opens the gate (they must be at the facility) and is the
                same code they'll use on the DJ site. Stored encrypted, so it can't be shown —
                set a new one and tell them.
                <span v-if="gateCodes.regulars_updated_at"><br>Last changed {{ formatCodeDate(gateCodes.regulars_updated_at) }}</span>
              </p>
              <div class="code-set-row">
                <input type="tel" inputmode="numeric" maxlength="4" v-model="newRegularsCode" placeholder="New 4-digit" class="code-input">
                <w-button bg-color="info" width="90" height="40" shadow text sm :disabled="gateCodesBusy" @click="setRegularsCode()">Set</w-button>
              </div>
            </div>

            <!-- Guest -->
            <div class="code-card">
              <div class="code-card-head">
                <span class="code-role">Guest code (today)</span>
                <span class="code-value">{{ gateCodes.guest || '––––' }}</span>
              </div>
              <p class="code-hint">
                Hand this to today's visitors. Rotates automatically each morning.
                <span v-if="gateCodes.guest_expires_at"><br>Valid until {{ formatCodeExpiry(gateCodes.guest_expires_at) }}</span>
              </p>
              <div class="code-set-row">
                <w-button bg-color="warning" width="150" height="40" shadow text sm :disabled="gateCodesBusy" @click="regenerateGuestCode()">Regenerate now</w-button>
              </div>
            </div>
          </div>
        </div>

        <!--Gate History Screen-->
        <div v-show="page3">
          <div class="center-screen">
            <h1>Gate History</h1>
          </div>
          <br>

          <!-- Date Selectors -->
          <div class="center-screen">
            <div class="date-selector-container">
              <div class="date-input-group">
                <label for="startDate">Start Date:</label>
                <input type="date" id="startDate" v-model="startDate" class="date-input" />
              </div>
              <div class="mediumspace"></div>
              <div class="date-input-group">
                <label for="endDate">End Date:</label>
                <input type="date" id="endDate" v-model="endDate" class="date-input" />
              </div>
            </div>
          </div>
          <br>

          <!-- Load Button -->
          <div class="center-screen">
            <w-button class="ma1" bg-color="success" width="200" height="60" shadow text xl @click="loadHistory"
              :disabled="isLoading || !startDate || !endDate">
              Load History
            </w-button>
          </div>

          <!-- Back Button -->
          <div class="center-screen">
            <br>
            <w-button class="ma1" bg-color="error" width="200" height="50" shadow text lg @click="backToGateControl">
              Back
            </w-button>
          </div>

          <!-- History Table -->
          <div v-if="historyData.length > 0" class="history-table-container">
            <br>
            <w-table :headers="historyHeaders" :items="historyData" :loading="isLoading">
            </w-table>
          </div>

          <!-- No Data Message -->
          <div v-else-if="historyLoaded && historyData.length === 0" class="center-screen">
            <br>
            <h3>No history data found for the selected date range.</h3>
          </div>
        </div>

        <!--Show Users Screen-->
        <div v-show="page4">
          <div class="center-screen">
            <h1>User Management</h1>
          </div>
          <br>

          <!-- User Filter Options -->
          <div class="center-screen">
            <div class="user-filter-container">
              <w-button class="ma1" :bg-color="userFilter === 'active' ? 'success' : 'grey'" width="120" height="50"
                shadow text md @click="setUserFilter('active')">
                Active Users
              </w-button>
              <w-button class="ma1" :bg-color="userFilter === 'expired' ? 'warning' : 'grey'" width="120" height="50"
                shadow text md @click="setUserFilter('expired')">
                Expired Users
              </w-button>
              <w-button class="ma1" :bg-color="userFilter === 'all' ? 'info' : 'grey'" width="120" height="50" shadow
                text md @click="setUserFilter('all')">
                All Users
              </w-button>
            </div>
          </div>
          <br>

          <!-- Action Buttons -->
          <div class="center-screen">
            <div class="button-group">
              <w-button class="ma1" bg-color="error" width="150" height="50" shadow text lg @click="backToGateControl">
                Back
              </w-button>
              <w-button class="ma1" bg-color="primary" width="150" height="50" shadow text lg @click="showAddUser">
                Add User
              </w-button>
              <w-button class="ma1" :bg-color="isEditButtonEnabled ? 'warning' : 'grey'" width="150" height="50" shadow
                text lg @click="showEditUser" :disabled="!isEditButtonEnabled">
                Edit User
              </w-button>
              <w-button class="ma1" :bg-color="isEditButtonEnabled ? 'info' : 'grey'" width="150" height="50" shadow
                text lg @click="resendLink" :disabled="!isEditButtonEnabled">
                Resend Link
              </w-button>
              <w-button class="ma1" :bg-color="isEditButtonEnabled ? 'primary' : 'grey'" width="150" height="50" shadow
                text lg @click="openIssueNewLinkOverlay" :disabled="!isEditButtonEnabled">
                Issue New Link
              </w-button>
              <w-button class="ma1" :bg-color="isEditButtonEnabled ? 'secondary' : 'grey'" width="150" height="50" shadow
                text lg @click="openManageUserGroups" :disabled="!isEditButtonEnabled">
                Manage Groups
              </w-button>
            </div>
          </div>

          <!-- Users Table -->
          <div v-if="usersData.length > 0" class="history-table-container">
            <br>
            <w-table :headers="usersHeaders" :items="usersData" :loading="isLoading" selectable-rows="1"
              @row-select="onUserSelect">
            </w-table>
          </div>

          <!-- No Data Message -->
          <div v-else-if="usersLoaded && usersData.length === 0" class="center-screen">
            <br>
            <h3>No {{ userFilter === 'all' ? '' : userFilter }} users found.</h3>
          </div>
        </div>

        <!--Add User Screen-->
        <div v-show="page5">
          <div class="center-screen">
            <h1>Add New User</h1>
          </div>
          <br>

          <!-- User Form -->
          <div class="form-container">
            <!-- Name Field -->
            <div class="form-field">
              <label for="userName">Full Name *</label>
              <input type="text" id="userName" v-model="newUser.name" class="form-input" placeholder="Enter full name"
                maxlength="100">
            </div>

            <!-- Phone Field -->
            <div class="form-field">
              <label for="userPhone">Phone Number *</label>
              <input type="tel" id="userPhone" v-model="newUser.phone" class="form-input"
                placeholder="+1234567890 (international format)" @blur="formatPhoneNumber">
            </div>

            <!-- Expires At Field -->
            <div class="form-field">
              <label for="userExpires">Expires At *</label>
              <input type="datetime-local" id="userExpires" v-model="newUser.expires_at" class="form-input">
            </div>

            <!-- Manager Checkbox -->
            <div class="form-field checkbox-field">
              <input type="checkbox" id="userManager" v-model="newUser.is_manager" class="form-checkbox">
              <label for="userManager">Manager privileges</label>
            </div>

            <!-- Auto Renew Checkbox -->
            <div class="form-field checkbox-field">
              <input type="checkbox" id="userAutoRenew" v-model="newUser.auto_renew" class="form-checkbox">
              <label for="userAutoRenew">Auto renew</label>
            </div>

            <!-- Group Selector -->
            <div class="form-field">
              <label for="userGroup">Add to Group (optional)</label>
              <select id="userGroup" v-model="newUser.group_id" class="form-input">
                <option :value="null">None</option>
                <option v-for="group in availableGroups" :key="group.id" :value="group.id">{{ group.name }}</option>
              </select>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="center-screen">
            <div class="button-group">
              <w-button class="ma1" bg-color="error" width="200" height="60" shadow text xl
                @click="backToUserManagement">
                Cancel
              </w-button>
              <w-button class="ma1" bg-color="success" width="200" height="60" shadow text xl @click="createUser"
                :disabled="!isFormValid || isLoading">
                Create User
              </w-button>
            </div>
          </div>
        </div>

        <!--Groups List Screen-->
        <div v-show="page6">
          <div class="center-screen">
            <h1>Groups</h1>
          </div>
          <br>

          <!-- Action Buttons -->
          <div class="center-screen">
            <div class="button-group">
              <w-button class="ma1" bg-color="error" width="150" height="50" shadow text lg @click="backToGateControl">
                Back
              </w-button>
              <w-button class="ma1" bg-color="success" width="150" height="50" shadow text lg
                @click="showCreateGroupOverlay = true; newGroupName = ''">
                New Group
              </w-button>
              <w-button class="ma1" :bg-color="selectedGroup ? 'info' : 'grey'" width="150" height="50" shadow text lg
                @click="viewGroupDetail" :disabled="!selectedGroup">
                View Group
              </w-button>
              <w-button class="ma1" :bg-color="selectedGroup ? 'warning' : 'grey'" width="150" height="50" shadow text lg
                @click="confirmDeleteGroup" :disabled="!selectedGroup">
                Delete Group
              </w-button>
            </div>
          </div>

          <!-- Groups Table -->
          <div v-if="groupsData.length > 0" class="history-table-container">
            <br>
            <w-table :headers="groupsHeaders" :items="groupsData" :loading="isLoading" selectable-rows="1"
              @row-select="onGroupSelect">
            </w-table>
          </div>

          <!-- No Data Message -->
          <div v-else-if="groupsLoaded && groupsData.length === 0" class="center-screen">
            <br>
            <h3>No groups found. Create one to get started.</h3>
          </div>
        </div>

        <!--Group Detail Screen-->
        <div v-show="page7">
          <div class="center-screen">
            <h1>{{ currentGroup ? currentGroup.name : 'Group' }}</h1>
          </div>
          <br>

          <!-- Action Buttons -->
          <div class="center-screen">
            <div class="button-group">
              <w-button class="ma1" bg-color="error" width="140" height="50" shadow text lg @click="backToGroups">
                Back
              </w-button>
              <w-button class="ma1" bg-color="success" width="140" height="50" shadow text lg
                @click="openAddMemberOverlay">
                Add Member
              </w-button>
              <w-button class="ma1" :bg-color="selectedMemberInGroup ? 'warning' : 'grey'" width="165" height="50"
                shadow text lg @click="removeMemberFromGroup" :disabled="!selectedMemberInGroup">
                Remove Member
              </w-button>
              <w-button class="ma1" bg-color="primary" width="140" height="50" shadow text lg
                @click="openSendLinksOverlay" :disabled="groupMembers.length === 0">
                Send Links
              </w-button>
            </div>
          </div>

          <!-- Members Table -->
          <div v-if="groupMembers.length > 0" class="history-table-container">
            <br>
            <w-table :headers="groupMembersHeaders" :items="groupMembers" :loading="isLoading" selectable-rows="1"
              @row-select="onGroupMemberSelect">
            </w-table>
          </div>

          <!-- No Data Message -->
          <div v-else-if="groupMembersLoaded && groupMembers.length === 0" class="center-screen">
            <br>
            <h3>No members in this group yet.</h3>
          </div>
        </div>

      </main>

      <!--Overlays-->

      <!--Create Group Overlay-->
      <w-overlay v-model="showCreateGroupOverlay" :z-index="10010" bg-color="rgba(0, 0, 0, 0.5)">
        <div class="edit-user-container">
          <div class="edit-user-header">
            <h2>Create Group</h2>
          </div>
          <div class="form-container">
            <div class="form-field">
              <label for="groupName">Group Name *</label>
              <input type="text" id="groupName" v-model="newGroupName" class="form-input"
                placeholder="e.g. Friday Night Band" maxlength="100">
            </div>
          </div>
          <div class="edit-user-buttons">
            <w-button class="ma1" bg-color="error" width="150" height="50" shadow text lg
              @click="showCreateGroupOverlay = false">Cancel</w-button>
            <w-button class="ma1" bg-color="success" width="150" height="50" shadow text lg
              @click="createGroup" :disabled="!newGroupName.trim() || isLoading">
              Create</w-button>
          </div>
        </div>
      </w-overlay>

      <!--Delete Group Confirmation Overlay-->
      <w-overlay v-model="showDeleteGroupConfirm" :z-index="10010" bg-color="rgba(0, 0, 0, 0.5)">
        <div class="edit-user-container">
          <div class="edit-user-header">
            <h2>Delete Group</h2>
          </div>
          <div class="form-container">
            <p style="text-align: center; font-size: 16px;">
              Are you sure you want to delete "{{ groupToDelete ? groupToDelete.name : '' }}"?
            </p>
            <p style="text-align: center; font-size: 14px; color: var(--color-text-muted);">
              This will not delete the users, only the group.
            </p>
          </div>
          <div class="edit-user-buttons">
            <w-button class="ma1" bg-color="info" width="150" height="50" shadow text lg
              @click="showDeleteGroupConfirm = false">Cancel</w-button>
            <w-button class="ma1" bg-color="error" width="150" height="50" shadow text lg
              @click="deleteGroup" :disabled="isLoading">
              Delete</w-button>
          </div>
        </div>
      </w-overlay>

      <!--Add Member Overlay-->
      <w-overlay v-model="showAddMemberOverlay" :z-index="10010" bg-color="rgba(0, 0, 0, 0.5)">
        <div class="edit-user-container" style="max-width: 700px;">
          <div class="edit-user-header">
            <h2>Add Member to {{ currentGroup ? currentGroup.name : 'Group' }}</h2>
          </div>
          <div style="padding: 20px;">
            <div v-if="allUsersForGroup.length > 0">
              <w-table :headers="[
                { label: 'Name', key: 'name' },
                { label: 'Phone', key: 'phone' },
                { label: 'Status', key: 'status' }
              ]" :items="allUsersForGroup" selectable-rows="1" @row-select="onAddMemberSelect">
              </w-table>
            </div>
            <div v-else class="center-screen">
              <p>No users available to add.</p>
            </div>
          </div>
          <div class="edit-user-buttons">
            <w-button class="ma1" bg-color="error" width="150" height="50" shadow text lg
              @click="showAddMemberOverlay = false">Cancel</w-button>
            <w-button class="ma1" bg-color="success" width="150" height="50" shadow text lg
              @click="addMemberToGroup" :disabled="!selectedMemberToAdd || isLoading">
              Add</w-button>
          </div>
        </div>
      </w-overlay>

      <!--Send Links Overlay-->
      <w-overlay v-model="showSendLinksOverlay" :z-index="10010" bg-color="rgba(0, 0, 0, 0.5)">
        <div class="edit-user-container" style="max-width: 700px;">
          <div class="edit-user-header">
            <h2>Send Links to {{ currentGroup ? currentGroup.name : 'Group' }}</h2>
          </div>

          <!-- Before send -->
          <div v-if="!sendLinksResults" style="padding: 20px;">
            <p style="text-align: center; font-size: 16px; margin-bottom: 10px;">
              {{ groupMembers.length }} member{{ groupMembers.length !== 1 ? 's' : '' }} in this group
            </p>
            <p v-if="!sendLinksNewLinks" style="text-align: center; font-size: 14px; color: var(--color-text-muted); margin-bottom: var(--space-lg);">
              Active users will be resent their existing link without changing their expiration. Expired/inactive users will have their expiration updated.
            </p>
            <p v-else style="text-align: center; font-size: 14px; color: var(--color-copper); margin-bottom: var(--space-lg);">
              All members will receive a new link. Their current links will be invalidated.
            </p>
            <div class="form-container" style="box-shadow: none;">
              <div class="form-field checkbox-field">
                <input type="checkbox" id="sendLinksNewLinksCheck" v-model="sendLinksNewLinks" class="form-checkbox">
                <label for="sendLinksNewLinksCheck">Issue new links (regenerate UUIDs)</label>
              </div>
              <div class="form-field">
                <label for="sendLinksExpires">{{ sendLinksNewLinks ? 'Expiration *' : 'Expiration for inactive users *' }}</label>
                <input type="datetime-local" id="sendLinksExpires" v-model="sendLinksExpiration" class="form-input">
              </div>
            </div>
          </div>

          <!-- After send -->
          <div v-else style="padding: var(--space-lg);">
            <div style="text-align: center; margin-bottom: var(--space-md);">
              <p class="send-results-text">Results</p>
              <p class="send-results-success">Sent: {{ sendLinksResults.sent }}</p>
              <p v-if="sendLinksResults.failed > 0" class="send-results-fail">Failed: {{ sendLinksResults.failed }}</p>
              <p>Total: {{ sendLinksResults.total }}</p>
            </div>
            <div v-if="sendLinksResults.details && sendLinksResults.details.length > 0"
              style="max-height: 300px; overflow-y: auto;">
              <div v-for="(detail, idx) in sendLinksResults.details" :key="idx"
                :style="{ padding: 'var(--space-sm)', borderBottom: '1px solid var(--color-rope)', color: detail.status === 'failed' ? '#C62828' : 'var(--color-copper)' }">
                <strong>{{ detail.name }}</strong> ({{ detail.phone }}) — {{ detail.reason }}
              </div>
            </div>
          </div>

          <div class="edit-user-buttons">
            <w-button v-if="!sendLinksResults" class="ma1" bg-color="error" width="150" height="50" shadow text lg
              @click="closeSendLinksOverlay">Cancel</w-button>
            <w-button v-if="!sendLinksResults" class="ma1" bg-color="success" width="150" height="50" shadow text lg
              @click="sendLinksToGroup"
              :disabled="!sendLinksExpiration || sendLinksInProgress">
              Send</w-button>
            <w-button v-if="sendLinksResults" class="ma1" bg-color="info" width="150" height="50" shadow text lg
              @click="closeSendLinksOverlay">Close</w-button>
          </div>
        </div>
      </w-overlay>

      <!--Error Overlay-->
      <w-overlay v-model="error" :z-index="10020" bg-color="rgba(250, 250, 250, 0.8)">
        <div class="center stream">
          <h1>Error</h1>
          <h2>Result: {{ this.result }}</h2>
          <w-button bg-color="error" xl dark width="200" @click="error = false"> <w-icon
              class="mr2">wi-cross</w-icon>Cancel</w-button>
        </div>
      </w-overlay>

      <!--Success Overlay-->
      <w-overlay v-model="success" :z-index="10015" bg-color="rgba(250, 250, 250, 0.8)">
        <div class="center stream">
          <h1>Operation Successful!</h1>
          <img src="./assets/check.png" height="150">
          <h2>Gate command executed successfully</h2>
          <w-button bg-color="success" xl dark width="200" @click="success = false"> <w-icon
              class="mr2">wi-check</w-icon>Close</w-button>
        </div>
      </w-overlay>

      <!--Edit User Overlay-->
      <w-overlay v-model="showEditOverlay" :z-index="10010" bg-color="rgba(0, 0, 0, 0.5)">
        <div class="edit-user-container">
          <div class="edit-user-header">
            <h2>Edit User</h2>
          </div>

          <!-- Edit User Form -->
          <div class="form-container">
            <!-- Name Field -->
            <div class="form-field">
              <label for="editUserName">Full Name *</label>
              <input type="text" id="editUserName" v-model="editUser.name" class="form-input"
                placeholder="Enter full name" maxlength="100">
            </div>

            <!-- Phone Field -->
            <div class="form-field">
              <label for="editUserPhone">Phone Number *</label>
              <input type="tel" id="editUserPhone" v-model="editUser.phone" class="form-input"
                placeholder="+1234567890 (international format)" @blur="formatEditPhoneNumber">
            </div>

            <!-- New UUID Checkbox -->
            <div class="form-field checkbox-field">
              <input type="checkbox" id="editUserNewUuid" v-model="editUser.new_uuid" class="form-checkbox">
              <label for="editUserNewUuid">Generate new UUID (will send new access link via SMS)</label>
            </div>

            <!-- Change Expires At Checkbox -->
            <div class="form-field checkbox-field">
              <input type="checkbox" id="editUserChangeExpires" v-model="editUser.change_expires_at"
                class="form-checkbox">
              <label for="editUserChangeExpires">Update expiration date</label>
            </div>

            <!-- Expires At Field (conditional) -->
            <div v-if="editUser.change_expires_at" class="form-field">
              <label for="editUserExpires">New Expires At *</label>
              <input type="datetime-local" id="editUserExpires" v-model="editUser.expires_at" class="form-input">
            </div>

            <!-- Manager Checkbox -->
            <div class="form-field checkbox-field">
              <input type="checkbox" id="editUserManager" v-model="editUser.is_manager" class="form-checkbox">
              <label for="editUserManager">Manager privileges</label>
            </div>

            <!-- Auto Renew Checkbox -->
            <div class="form-field checkbox-field">
              <input type="checkbox" id="editUserAutoRenew" v-model="editUser.auto_renew" class="form-checkbox">
              <label for="editUserAutoRenew">Auto renew</label>
            </div>

            <!-- Revocation gotcha warning: a past expires_at with auto_renew
                 still on does not actually revoke; check_gate_access will
                 auto-extend on the user's next request. -->
            <div v-if="autoRenewRevokeGotcha" class="auto-renew-warning">
              <strong>Warning:</strong> Auto-renew is still on and the new
              expiration is in the past. This user will be automatically
              re-activated on their next gate access. To fully revoke them,
              uncheck <em>Auto renew</em> as well.
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="edit-user-buttons">
            <w-button class="ma1" bg-color="error" width="150" height="50" shadow text lg @click="closeEditOverlay">
              Cancel
            </w-button>
            <w-button class="ma1" bg-color="success" width="150" height="50" shadow text lg @click="updateUser"
              :disabled="!isEditFormValid || isLoading">
              Update User
            </w-button>
          </div>
        </div>
      </w-overlay>

      <!--Manage User Groups Overlay-->
      <w-overlay v-model="showManageUserGroupsOverlay" :z-index="10010" bg-color="rgba(0, 0, 0, 0.5)">
        <div class="edit-user-container" style="max-width: 600px;">
          <div class="edit-user-header">
            <h2>Groups for {{ selectedUser ? selectedUser.name : '' }}</h2>
          </div>
          <div style="padding: var(--space-lg);">
            <!-- Current groups -->
            <div class="manage-groups-section">
              <h3>Member of:</h3>
              <div v-if="userGroups.length > 0">
                <div v-for="group in userGroups" :key="group.id"
                  style="display: flex; justify-content: space-between; align-items: center; padding: var(--space-sm) var(--space-md); border-bottom: var(--border-subtle);">
                  <span>{{ group.name }}</span>
                  <w-button bg-color="error" sm shadow text @click="removeUserFromGroup(group.id)"
                    :disabled="isLoading">Remove</w-button>
                </div>
              </div>
              <p v-else class="manage-groups-empty">Not in any groups</p>
            </div>

            <!-- Add to group -->
            <div class="manage-groups-section" style="margin-top: var(--space-lg);">
              <h3>Add to group:</h3>
              <div v-if="userAvailableGroups.length > 0">
                <div v-for="group in userAvailableGroups" :key="group.id"
                  style="display: flex; justify-content: space-between; align-items: center; padding: var(--space-sm) var(--space-md); border-bottom: var(--border-subtle);">
                  <span>{{ group.name }}</span>
                  <w-button bg-color="success" sm shadow text @click="addUserToGroup(group.id)"
                    :disabled="isLoading">Add</w-button>
                </div>
              </div>
              <p v-else class="manage-groups-empty">{{ userGroups.length > 0 ? 'In all groups' : 'No groups exist yet' }}</p>
            </div>
          </div>
          <div class="edit-user-buttons">
            <w-button class="ma1" bg-color="info" width="150" height="50" shadow text lg
              @click="showManageUserGroupsOverlay = false">Close</w-button>
          </div>
        </div>
      </w-overlay>

      <!--Issue New Link Overlay-->
      <w-overlay v-model="showIssueNewLinkOverlay" :z-index="10010" bg-color="rgba(0, 0, 0, 0.5)">
        <div class="edit-user-container">
          <div class="edit-user-header">
            <h2>Issue New Link</h2>
          </div>
          <div class="form-container">
            <p style="text-align: center; font-size: 14px; color: var(--color-text-muted); margin-bottom: var(--space-md);">
              This will generate a new access link for {{ selectedUser ? selectedUser.name : '' }} and invalidate their current link.
            </p>
            <div class="form-field">
              <label for="issueNewLinkExpires">Expiration *</label>
              <input type="datetime-local" id="issueNewLinkExpires" v-model="issueNewLinkExpiration" class="form-input">
            </div>
          </div>
          <div class="edit-user-buttons">
            <w-button class="ma1" bg-color="error" width="150" height="50" shadow text lg
              @click="showIssueNewLinkOverlay = false">Cancel</w-button>
            <w-button class="ma1" bg-color="success" width="150" height="50" shadow text lg
              @click="issueNewLink" :disabled="!issueNewLinkExpiration || isLoading">
              Issue</w-button>
          </div>
        </div>
      </w-overlay>

      <!--Loading Overlay-->
      <w-overlay v-model="isLoading" persistent :z-index="10030" bg-color="rgba(250, 250, 250, 0.8)">
        <div class="loading-spinner">
          <img src="./assets/spinner2.gif" alt="Loading..." />
        </div>
      </w-overlay>

    </w-app>
  </div>
</template>

<script>
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.VUE_APP_SUPABASE_URL || 'https://jkjjytdqcywpwsarllsy.supabase.co';
// This app uses UUID-token auth (not Supabase Auth), and may be loaded from
// embeds where localStorage access throws "the operation is insecure".
// Disable all auth persistence so createClient never touches storage.
let supabase = null;
try {
  supabase = createClient(SUPABASE_URL, process.env.VUE_APP_SUPABASE_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  });
} catch (e) {
  // Realtime gate-status updates will simply fall back to the 30s status poll.
  console.warn('Supabase client init failed; realtime disabled', e);
}

export default {
  name: 'App',
  data: () => ({
    page1: false, // Authentication failure page (legacy; keypad is the default unauth screen)
    keypadVisible: true, // Keypad code-entry — the primary sign-in screen
    codeDigits: '', // digits typed on the keypad
    codeError: '', // inline keypad error message
    codeSubmitting: false, // guards double-submit while a code is verifying
    pendingSubmit: false, // 4 digits entered, waiting for the Turnstile token to arrive
    turnstileToken: null, // Cloudflare Turnstile token (only when configured)
    turnstileWidgetId: null,
    geoCoords: null, // {lat, lng, accuracy} for the employee/guest geofence check
    geoStatus: 'unknown', // 'ok' | 'denied' | 'unavailable' | 'unsupported' | 'unknown'
    page2: false, // Gate control page
    page3: false,
    page4: false,
    page5: false,
    error: false,
    isLoading: false,
    success: false,
    result: '',
    accessToken: null,
    isAuthenticated: false,
    gateStatus: 'Closed', // Initial status
    gateToggling: false, // Prevents double-tap race condition
    gateOffline: false, // True when Shelly device is unreachable
    gateMode: 'default_closed', // 'default_closed' or 'default_open'
    autoCloseAt: null, // Server-side auto-close deadline (ISO) from gate-control; null = none armed
    autoCloseCountdown: null, // Seconds remaining until autoCloseAt (display only)
    autoCloseTicker: null, // setInterval that recomputes the countdown from autoCloseAt
    gateOpenSince: null, // Date when gate was opened
    gateOpenDuration: null, // Formatted "M:SS"
    gateOpenDurationTimer: null, // setInterval reference
    transitionState: null, // null | 'opening' | 'closing' — sensor not yet confirmed after toggle
    transitionDeadline: 0,
    transitionPollTimer: null,
    transitionGraceSeconds: 30, // how long to wait for sensor to confirm before flagging failure
    gateRealtimeChannel: null, // Supabase Realtime channel for instant Shelly state pushes
    gateAuthUrl: process.env.VUE_APP_GATE_AUTH,
    gateControlUrl: process.env.VUE_APP_GATE_CONTROL,
    getTodayUrl: process.env.VUE_APP_GET_TODAY,
    getHistoryUrl: process.env.VUE_APP_GET_HISTORY,
    getUsersUrl: process.env.VUE_APP_GET_USERS,
    createUserUrl: process.env.VUE_APP_CREATE_USER,
    editUserUrl: process.env.VUE_APP_EDIT_USER,
    resendLinkUrl: process.env.VUE_APP_RESEND_LINK,
    gateCodeAuthUrl: process.env.VUE_APP_GATE_CODE_AUTH,
    gateCodesAdminUrl: process.env.VUE_APP_GATE_CODES_ADMIN,
    turnstileSiteKey: process.env.VUE_APP_TURNSTILE_SITE_KEY || '',
    name: null,
    isManager: false,
    startDate: null,
    endDate: null,
    historyData: [],
    historyLoaded: false,
    sessionTimeout: null, // For auto-logout
    statusPollTimer: null, // Interval for periodic status polling
    historyHeaders: [
      { label: 'Event Time', key: 'event_time' },
      { label: 'Details', key: 'details' },
      { label: 'Duration', key: 'duration' }
    ],
    usersData: [],
    usersLoaded: false,
    userFilter: 'active', // 'active', 'expired', 'all'
    selectedUser: null, // Track selected user for editing
    usersHeaders: [
      { label: 'Name', key: 'name' },
      { label: 'Phone', key: 'phone' },
      { label: 'Expires At', key: 'expires_at' },
      { label: 'Created At', key: 'created_at' },
      { label: 'Manager', key: 'is_manager' },
      { label: 'Auto Renew', key: 'auto_renew' },
      { label: 'Status', key: 'status' }
    ],
    // New user form data
    newUser: {
      name: '',
      phone: '',
      expires_at: '',
      is_manager: false,
      auto_renew: false,
      group_id: null
    },
    // Edit user form data and state
    editUser: {
      user_id: null,
      name: '',
      phone: '',
      expires_at: '',
      is_manager: false,
      auto_renew: false,
      new_uuid: false,
      change_expires_at: false
    },
    showEditOverlay: false,
    // Issue New Link
    showIssueNewLinkOverlay: false,
    issueNewLinkExpiration: '',
    // Groups
    page6: false, // Groups list
    page7: false, // Group detail
    gateGroupsUrl: process.env.VUE_APP_GATE_GROUPS,
    // Gate Codes (manager)
    page8: false, // Gate codes management
    // manager + regulars are bcrypt-hashed in app_pins — there is no read-back, so we
    // only ever hold their "last changed" metadata. Guest stays plaintext by design.
    gateCodes: { guest: '', guest_valid_date: null, guest_expires_at: null, manager_updated_at: null, regulars_updated_at: null },
    gateCodesLoaded: false,
    gateCodesBusy: false,
    gateCodesError: '',
    newRegularsCode: '',
    groupsData: [],
    groupsLoaded: false,
    selectedGroup: null,
    currentGroup: null,
    groupMembers: [],
    groupMembersLoaded: false,
    selectedMemberInGroup: null,
    showCreateGroupOverlay: false,
    newGroupName: '',
    showDeleteGroupConfirm: false,
    groupToDelete: null,
    showAddMemberOverlay: false,
    allUsersForGroup: [],
    selectedMemberToAdd: null,
    showSendLinksOverlay: false,
    sendLinksExpiration: '',
    sendLinksResults: null,
    sendLinksInProgress: false,
    sendLinksNewLinks: false,
    availableGroups: [],
    showManageUserGroupsOverlay: false,
    userGroups: [],
    userAvailableGroups: [],
    groupsHeaders: [
      { label: 'Name', key: 'name' },
      { label: 'Members', key: 'member_count' },
      { label: 'Created', key: 'created_at' }
    ],
    groupMembersHeaders: [
      { label: 'Name', key: 'name' },
      { label: 'Phone', key: 'phone' },
      { label: 'Expires At', key: 'expires_at' },
      { label: 'Status', key: 'status' }
    ]
  }),

  computed: {
    isFormValid() {
      return this.newUser.name.trim() &&
        this.newUser.phone.trim() &&
        this.newUser.expires_at &&
        this.isValidPhoneNumber(this.newUser.phone);
    },

    isEditFormValid() {
      return this.editUser.name.trim() &&
        this.editUser.phone.trim() &&
        this.isValidPhoneNumber(this.editUser.phone) &&
        (!this.editUser.change_expires_at || this.editUser.expires_at);
    },

    isEditButtonEnabled() {
      return this.selectedUser !== null;
    },

    // The "auto-renew revocation gotcha": setting expires_at to past while
    // auto_renew is still on does NOT actually revoke the user — they'll be
    // auto-renewed by check_gate_access on their next request. True if the
    // operator is about to walk into that trap so the UI can warn them.
    autoRenewRevokeGotcha() {
      if (!this.editUser.change_expires_at) return false;
      if (!this.editUser.auto_renew) return false;
      if (!this.editUser.expires_at) return false;
      const newExpires = new Date(this.editUser.expires_at).getTime();
      if (!Number.isFinite(newExpires)) return false;
      return newExpires <= Date.now();
    },

    gateDisplayStatus() {
      if (this.gateOffline) return 'Offline';
      if (this.transitionState === 'opening') return 'Opening';
      if (this.transitionState === 'closing') return 'Closing';
      return this.gateStatus;
    },

    gateActionLabel() {
      if (this.gateOffline) return 'Retry Connection';
      if (this.transitionState === 'opening') return 'Opening...';
      if (this.transitionState === 'closing') return 'Closing...';
      return this.gateStatus === 'Open' ? 'Close Gate' : 'Open Gate';
    },

    gateActionDisabled() {
      return this.isLoading || this.gateToggling || this.transitionState !== null ||
        (!this.isManager && this.gateMode === 'default_open');
    }
  },

  async mounted() {
    await this.checkAuth();
    this.setupSessionManagement();
    // Don't load default dates here - only load them when history page is accessed
  },

  methods: {
    // Setup session management and activity tracking
    setupSessionManagement() {
      // Auto-logout after 10 minutes of inactivity. Short window because a
      // gate-control session carries physical-access authority — a logged-in
      // tab left open on a shared device should expire fast.
      const SESSION_TIMEOUT = 10 * 60 * 1000;

      const resetTimer = () => {
        if (this.sessionTimeout) {
          clearTimeout(this.sessionTimeout);
        }
        if (this.isAuthenticated) {
          this.sessionTimeout = setTimeout(() => {
            this.logout('Session expired due to inactivity');
          }, SESSION_TIMEOUT);
        }
      };

      // Throttle for high-frequency events (mousemove, scroll) so we don't
      // call resetTimer hundreds of times per second on desktops. A 1s
      // throttle is fine — we just need ANY activity within the 10min window
      // to keep the session alive.
      let throttleTimer = null;
      const throttledReset = () => {
        if (throttleTimer) return;
        throttleTimer = setTimeout(() => {
          throttleTimer = null;
          resetTimer();
        }, 1000);
      };

      // Discrete events reset immediately; mousemove/scroll go through the throttle.
      ['mousedown', 'keypress', 'touchstart', 'click'].forEach(event => {
        document.addEventListener(event, resetTimer, true);
      });
      ['mousemove', 'scroll'].forEach(event => {
        document.addEventListener(event, throttledReset, true);
      });

      // Initial timer setup
      if (this.isAuthenticated) {
        resetTimer();
      }
    },

    // Logout function
    logout(message = 'Logged out') {
      this.isAuthenticated = false;
      this.accessToken = null;
      this.name = null;
      this.isManager = false;

      if (this.sessionTimeout) {
        clearTimeout(this.sessionTimeout);
        this.sessionTimeout = null;
      }

      // Clear gate timers
      this.stopAutoCloseTicker();
      this.autoCloseCountdown = null;
      this.stopDurationTimer();
      this.stopStatusPolling();
      this.stopGateRealtime();
      this.stopTransitionPolling();
      
      // Return to the keypad, surfacing any logout reason inline.
      this.showKeypad(message !== 'Logged out' ? message : '');
    },

    // HTML-escape for embedding in display strings ONLY (e.g. error messages
    // that get interpolated into HTML). DO NOT call this on data being sent
    // to the backend — Vue's {{ }} and w-table auto-escape on render, so
    // pre-escaping at the data layer would store "O&#x27;Brien" instead of
    // "O'Brien", showing the entities literally in the UI.
    escapeForDisplay(input) {
      if (typeof input !== 'string') return input;
      return input
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;');
    },

    // Validate token format (basic check)
    isValidTokenFormat(token) {
      if (!token || typeof token !== 'string') return false;
      // Check if token is a valid UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(token);
    },

    // Extract access token from URL parameters
    getAccessTokenFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token') || urlParams.get('access_token');
      
      // Clear the token from URL after extraction to prevent sharing/logging
      if (token && window.history && window.history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.delete('token');
        url.searchParams.delete('access_token');
        window.history.replaceState({}, document.title, url.toString());
      }
      
      return token;
    },

    // Entry point. If the URL carries an SMS token (?token=<uuid>), validate it
    // as before (backward-compatible fallback for existing links). Otherwise the
    // keypad is the primary sign-in.
    async checkAuth() {
      this.accessToken = this.getAccessTokenFromUrl();

      if (!this.accessToken) {
        this.showKeypad();
        return;
      }

      // Existing SMS link present — validate the UUID token via gate-auth-verify.
      this.isLoading = true;
      if (!this.isValidTokenFormat(this.accessToken)) {
        this.isLoading = false;
        this.showKeypad('That link looks invalid — enter your code instead.');
        return;
      }

      try {
        const response = await fetch(this.gateAuthUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY
          },
          body: JSON.stringify({ uuid: this.accessToken })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.authorized) {
          this.isAuthenticated = true;
          this.name = data.name;
          this.isManager = data.is_manager;
          this.keypadVisible = false;
          this.showGateControl();
        } else {
          this.showKeypad('That link is no longer valid — enter your code.');
        }
      } catch {
        this.showKeypad('Could not verify that link — enter your code.');
      } finally {
        this.isLoading = false;
      }
    },

    // Show the keypad sign-in screen, optionally surfacing a message.
    showKeypad(message = '') {
      this.isAuthenticated = false;
      this.keypadVisible = true;
      this.codeError = message;
      this.codeDigits = '';
      this.codeSubmitting = false;
      this.pageSwitch(0); // hide all authenticated pages (no map entry for 0)
      this.$nextTick(() => { this.ensureTurnstile(); this.ensureGeolocation(); });
    },

    // Keypad input handlers.
    pressDigit(n) {
      if (this.codeSubmitting || this.codeDigits.length >= 4) return;
      this.codeError = '';
      this.codeDigits += String(n);
      if (this.codeDigits.length === 4) this.submitCode();
    },
    pressBackspace() {
      if (this.codeSubmitting) return;
      this.pendingSubmit = false;
      this.codeDigits = this.codeDigits.slice(0, -1);
    },
    clearCode() {
      if (this.codeSubmitting) return;
      this.pendingSubmit = false;
      this.codeDigits = '';
      this.codeError = '';
    },

    // Verify a 4-digit code via gate-code-auth. On success we receive the role's
    // uuid and thread it through the app exactly like an SMS token.
    async submitCode() {
      if (this.codeSubmitting) return;
      if (!/^\d{4}$/.test(this.codeDigits)) return;
      // Don't verify until BOTH a 4-digit code is entered AND the Turnstile is green.
      // If the human-check isn't done yet, arm and wait — the Turnstile callback
      // re-invokes submitCode the moment the token arrives, so it submits itself.
      if (this.turnstileSiteKey && !this.turnstileToken) {
        this.pendingSubmit = true;
        this.codeError = '';
        return;
      }
      this.pendingSubmit = false;
      this.codeSubmitting = true;
      this.codeError = '';
      // Best-effort location — the server enforces the geofence for employee/guest codes.
      if (!this.geoCoords) {
        this.geoCoords = await this.getGeolocationOnce();
      }
      const payload = { code: this.codeDigits, turnstile_token: this.turnstileToken || undefined };
      if (this.geoCoords) {
        payload.lat = this.geoCoords.lat;
        payload.lng = this.geoCoords.lng;
        payload.accuracy = this.geoCoords.accuracy;
      }
      try {
        const response = await fetch(this.gateCodeAuthUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY
          },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));

        if (response.status === 429) {
          this.codeError = 'Too many attempts. Please wait a few minutes and try again.';
          this.resetAfterFailedCode();
          return;
        }
        if (response.status === 403) {
          // Turnstile rejected the token (e.g. it expired) — NOT a wrong code.
          this.codeError = 'Please complete the “I’m human” check below, then try again.';
          this.resetAfterFailedCode();
          return;
        }

        if (data.authorized) {
          this.accessToken = data.uuid;
          this.name = data.name;
          this.isManager = data.is_manager;
          this.isAuthenticated = true;
          this.keypadVisible = false;
          this.codeDigits = '';
          this.showGateControl();
        } else if (data.expired) {
          this.codeError = 'That code has expired. Ask a manager for today’s code.';
          this.resetAfterFailedCode();
        } else if (data.too_far) {
          this.codeError = 'You must be at the facility to use this code.';
          this.resetAfterFailedCode();
        } else if (data.location_required || data.location_imprecise) {
          // Tailor the guidance to WHY we have no usable location.
          if (this.geoStatus === 'denied') {
            this.codeError = 'Location is blocked for this site. Turn it on in your browser (tap the lock/ⓘ icon in the address bar → Location → Allow), then retry — or ask a manager to let you in.';
          } else if (this.geoStatus === 'unsupported') {
            this.codeError = 'This device can’t share its location. Ask a manager to let you in.';
          } else if (data.location_imprecise) {
            this.codeError = 'Couldn’t get a precise location — step into the open near the gate and try again.';
          } else {
            this.codeError = 'Couldn’t get your location — move to an open spot near the gate and try again.';
          }
          this.resetAfterFailedCode();
          this.geoCoords = null;
          // Re-requesting after a hard denial does nothing (browser won't re-prompt),
          // so only retry the fix when it might actually succeed.
          if (this.geoStatus !== 'denied' && this.geoStatus !== 'unsupported') this.ensureGeolocation();
        } else {
          this.codeError = 'Incorrect code. Please try again.';
          this.resetAfterFailedCode();
        }
      } catch {
        this.codeError = 'Could not reach the gate. Check your connection and try again.';
        this.resetAfterFailedCode();
      } finally {
        this.codeSubmitting = false;
      }
    },

    resetAfterFailedCode() {
      this.codeDigits = '';
      this.resetTurnstile();
    },

    // Load + render Cloudflare Turnstile only when a site key is configured.
    // With no site key (dev / pre-setup), the keypad works without it and the
    // edge function skips Turnstile too.
    ensureTurnstile() {
      if (!this.turnstileSiteKey) return;
      const render = () => {
        if (!window.turnstile || !this.$refs.turnstile || this.turnstileWidgetId !== null) return;
        try {
          this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstile, {
            sitekey: this.turnstileSiteKey,
            callback: (token) => { this.turnstileToken = token; if (this.pendingSubmit) this.submitCode(); },
            'expired-callback': () => { this.turnstileToken = null; },
            'error-callback': () => { this.turnstileToken = null; }
          });
        } catch (e) {
          console.warn('Turnstile render failed', e);
        }
      };
      if (window.turnstile) { render(); return; }
      if (!document.getElementById('cf-turnstile-script')) {
        const s = document.createElement('script');
        s.id = 'cf-turnstile-script';
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        s.async = true;
        s.defer = true;
        s.onload = render;
        document.head.appendChild(s);
      } else {
        const poll = setInterval(() => { if (window.turnstile) { clearInterval(poll); render(); } }, 200);
        setTimeout(() => clearInterval(poll), 5000);
      }
    },
    resetTurnstile() {
      this.turnstileToken = null;
      if (this.turnstileSiteKey && window.turnstile && this.turnstileWidgetId !== null) {
        try { window.turnstile.reset(this.turnstileWidgetId); } catch { /* ignore */ }
      }
    },

    // Warm up a location fix (triggers the one-time permission prompt) so the geofence
    // check is fast at submit. Only employees/guests are geofenced server-side; managers
    // are exempt, so a denied prompt still lets a manager in.
    ensureGeolocation() {
      if (typeof navigator === 'undefined' || !('geolocation' in navigator)) { this.geoStatus = 'unsupported'; return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => { this.geoCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }; this.geoStatus = 'ok'; },
        (err) => { this.geoStatus = (err && err.code === 1) ? 'denied' : 'unavailable'; },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
      );
    },
    getGeolocationOnce(timeout = 8000) {
      return new Promise((resolve) => {
        if (typeof navigator === 'undefined' || !('geolocation' in navigator)) { this.geoStatus = 'unsupported'; resolve(null); return; }
        navigator.geolocation.getCurrentPosition(
          (pos) => { this.geoStatus = 'ok'; resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); },
          (err) => { this.geoStatus = (err && err.code === 1) ? 'denied' : 'unavailable'; resolve(null); },
          { enableHighAccuracy: true, timeout, maximumAge: 30000 }
        );
      });
    },

    // Show authentication failure page
    showAuthFailure() {
      this.isAuthenticated = false;
      this.pageSwitch(1); // Show authentication failure page
    },

    // Show gate control page
    showGateControl() {
      this.isAuthenticated = true;
      this.pageSwitch(2); // Show gate control page
      this.fetchGateStatus(); // Best-effort fetch of current state
      this.startStatusPolling();
      this.startGateRealtime();
    },

    // Fetch current gate status from Shelly via edge function
    async fetchGateStatus(silent = false) {
      if (!silent) this.isLoading = true;
      try {
        const response = await fetch(this.gateControlUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'status' })
        });
        if (!response.ok) {
          this.gateOffline = true;
          return;
        }
        const data = await response.json();
        if (data.success) {
          this.gateOffline = false;
          const newStatus = data.status === 'on' ? 'Open' : 'Closed';
          const statusChanged = this.gateStatus !== newStatus;
          this.gateStatus = newStatus;
          this.gateMode = data.gate_mode || 'default_closed';
          this.applyAutoCloseAt(data.auto_close_at);

          // If we were waiting on the sensor to confirm a toggle, end the
          // transition the moment it agrees.
          if (this.transitionState === 'opening' && this.gateStatus === 'Open') {
            this.endTransition();
          } else if (this.transitionState === 'closing' && this.gateStatus === 'Closed') {
            this.endTransition();
          }

          // Only start/stop timers on initial load or when status actually changed
          if (!silent || statusChanged) {
            if (this.gateStatus === 'Open') {
              this.startDurationTimer();
            } else {
              this.stopDurationTimer();
            }
          }
        } else {
          this.gateOffline = true;
        }
      } catch {
        this.gateOffline = true;
      } finally {
        if (!silent) this.isLoading = false;
      }
    },

    // Backup poll. Realtime pushes via Shelly webhook are the primary signal;
    // this is a 30s heartbeat in case a webhook is dropped (Shelly Mini Gen3
    // WiFi is borderline at -89 dBm).
    startStatusPolling() {
      this.stopStatusPolling();
      this.statusPollTimer = setInterval(() => {
        if (this.page2 && !this.isLoading && !this.gateToggling) {
          this.fetchGateStatus(true);
        }
      }, 30000);
    },

    stopStatusPolling() {
      if (this.statusPollTimer) {
        clearInterval(this.statusPollTimer);
        this.statusPollTimer = null;
      }
    },

    // Subscribe to the 'gate-status' Realtime channel. gate-webhook (called by
    // Shelly on input.toggle_on/off + switch.on/off) broadcasts immediately
    // when the device changes state. No-op if supabase init failed (some
    // sandboxed embeds block storage); the 30s status poll still works.
    startGateRealtime() {
      this.stopGateRealtime();
      if (!supabase) return;
      try {
        const channel = supabase.channel('gate-status');
        channel.on('broadcast', { event: 'state' }, ({ payload }) => {
          this.applyRealtimeState(payload);
        });
        channel.subscribe();
        this.gateRealtimeChannel = channel;
      } catch (e) {
        console.warn('gate-status realtime subscribe failed', e);
      }
    },

    stopGateRealtime() {
      if (this.gateRealtimeChannel && supabase) {
        try { supabase.removeChannel(this.gateRealtimeChannel); } catch { /* ignore */ }
      }
      this.gateRealtimeChannel = null;
    },

    // Apply a webhook-pushed state update. Each Shelly webhook only carries
    // one field (the one that triggered it), so null fields mean 'no change'.
    applyRealtimeState(payload) {
      if (!payload || typeof payload !== 'object') return;
      // Server auto-released hold-open (gate detected closed) — reflect the
      // mode change so the button + indicator update without a round-trip.
      if (payload.gate_mode === 'default_open' || payload.gate_mode === 'default_closed') {
        this.gateMode = payload.gate_mode;
      }
      if (payload.sensor_closed === true || payload.sensor_closed === false) {
        const newStatus = payload.sensor_closed ? 'Closed' : 'Open';
        if (newStatus !== this.gateStatus) {
          this.gateStatus = newStatus;
          this.gateOffline = false;
          if (this.transitionState === 'opening' && newStatus === 'Open') this.endTransition();
          if (this.transitionState === 'closing' && newStatus === 'Closed') this.endTransition();
          if (newStatus === 'Open') {
            this.startDurationTimer();
          } else {
            this.stopDurationTimer();
          }
        }
      }
    },

    // Toggle gate function
    async toggleGate() {
      if (!this.isAuthenticated) {
        this.result = 'Not authenticated';
        this.error = true;
        return;
      }

      if (this.gateToggling) return;
      this.gateToggling = true;
      this.isLoading = true;

      try {
        const response = await fetch(this.gateControlUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY
          },
          body: JSON.stringify({ uuid: this.accessToken })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          if (data.toggled_to === 'on') {
            this.startTransition('opening');
          } else {
            this.startTransition('closing');
            // Backend reverts mode when closing in default_open
            if (data.mode_reverted) {
              this.gateMode = data.mode_reverted;
            }
            // Don't stop the auto-close countdown yet — keep showing it until
            // sensor confirms closed, in case the gate is mid-motion.
          }
        } else {
          throw new Error(data.error || 'Gate control failed');
        }

      } catch (error) {
        this.result = `Failed to control gate: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
        this.gateToggling = false;
      }
    },

    // Auto-close countdown. DISPLAY ONLY, but honest: the deadline (autoCloseAt) is
    // set and owned by the server, and the server's gate-auto-close sweep is what
    // actually sends the closing pulse. This just renders the time remaining toward
    // that real deadline — if this page is closed, the gate still closes.
    applyAutoCloseAt(iso) {
      this.autoCloseAt = iso || null;
      this.stopAutoCloseTicker();
      if (!this.autoCloseAt) {
        this.autoCloseCountdown = null;
        return;
      }
      const tick = () => {
        const remaining = Math.round((new Date(this.autoCloseAt).getTime() - Date.now()) / 1000);
        if (remaining > 0) {
          this.autoCloseCountdown = remaining;
        } else {
          // Deadline passed — the sweep closes it within ~15s. Stop counting; the
          // sensor will report the closed state and the UI follows from there.
          this.autoCloseCountdown = null;
          this.stopAutoCloseTicker();
        }
      };
      tick();
      this.autoCloseTicker = setInterval(tick, 1000);
    },
    stopAutoCloseTicker() {
      if (this.autoCloseTicker) {
        clearInterval(this.autoCloseTicker);
        this.autoCloseTicker = null;
      }
    },

    // Transition state (opening / closing) — set right after a successful
    // toggle, cleared by fetchGateStatus when the sensor confirms the new
    // position, or by the deadline if the sensor never agrees.
    startTransition(direction) {
      this.transitionState = direction;
      this.transitionDeadline = Date.now() + this.transitionGraceSeconds * 1000;
      this.startTransitionPolling();
    },

    endTransition() {
      this.transitionState = null;
      this.transitionDeadline = 0;
      this.stopTransitionPolling();
    },

    startTransitionPolling() {
      this.stopTransitionPolling();
      this.transitionPollTimer = setInterval(() => {
        if (this.transitionState === null) {
          this.stopTransitionPolling();
          return;
        }
        if (Date.now() > this.transitionDeadline) {
          const direction = this.transitionState;
          this.endTransition();
          this.result = `Gate did not finish ${direction} within ${this.transitionGraceSeconds}s. Check the gate.`;
          this.error = true;
          return;
        }
        this.fetchGateStatus(true);
      }, 1000);
    },

    stopTransitionPolling() {
      if (this.transitionPollTimer) {
        clearInterval(this.transitionPollTimer);
        this.transitionPollTimer = null;
      }
    },

    // Gate open duration tracking
    startDurationTimer() {
      this.stopDurationTimer();
      if (!this.gateOpenSince) {
        this.gateOpenSince = new Date();
      }
      this.updateDurationDisplay();
      this.gateOpenDurationTimer = setInterval(() => {
        this.updateDurationDisplay();
      }, 1000);
    },

    stopDurationTimer() {
      if (this.gateOpenDurationTimer) {
        clearInterval(this.gateOpenDurationTimer);
        this.gateOpenDurationTimer = null;
      }
      this.gateOpenSince = null;
      this.gateOpenDuration = null;
    },

    updateDurationDisplay() {
      if (!this.gateOpenSince) return;
      const elapsed = Math.floor((new Date() - this.gateOpenSince) / 1000);
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      this.gateOpenDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },

    // Set gate mode (manager only)
    async setGateMode(mode) {
      if (!this.isManager) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.gateControlUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'set_mode', mode })
        });
        const data = await response.json();
        if (data.success) {
          this.gateMode = mode;
          // Both directions cancel a pending auto-close server-side — holding
          // suppresses it, releasing closes the gate outright.
          this.applyAutoCloseAt(null);
          // Hold NEVER moves the gate (a relay command is a button press on this
          // hardware — that's what used to close it). Release does close it, but only
          // if the sensor said it was open; the server reports whether it pulsed.
          // Never infer gateStatus here — the sensor is the truth.
          if (data.pulsed) this.startTransition('closing');
        } else {
          throw new Error(data.error || 'Failed to set mode');
        }
      } catch (error) {
        this.result = `Failed to set gate mode: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // ===== Gate Codes (manager) =====
    async showGateCodes() {
      if (!this.isManager) return;
      this.gateCodesError = '';
      this.newRegularsCode = '';
      this.isLoading = true;
      try {
        await this.loadGateCodes();
        this.pageSwitch(8);
      } catch (error) {
        this.result = `Failed to load gate codes: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    async loadGateCodes() {
      const response = await fetch(this.gateCodesAdminUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
        body: JSON.stringify({ uuid: this.accessToken, action: 'get_codes' })
      });
      const data = await response.json();
      if (!data.authorized) throw new Error(data.error || 'Not authorized');
      const byRole = {};
      (data.codes || []).forEach(c => { byRole[c.role] = c; });
      this.gateCodes = {
        guest: byRole.guest ? byRole.guest.code : '',
        guest_valid_date: byRole.guest ? byRole.guest.valid_date : null,
        guest_expires_at: data.guest_expires_at || null,
        // manager/regulars are hashed — the server sends metadata only, never a value.
        manager_updated_at: byRole.manager ? byRole.manager.updated_at : null,
        regulars_updated_at: byRole.regulars ? byRole.regulars.updated_at : null
      };
      this.gateCodesLoaded = true;
    },

    // Only the regulars code is settable here. The manager PIN is the same value
    // station49aws uses, so it's deliberately managed there — the gate must not be
    // able to silently rotate an aws-wide credential.
    async setRegularsCode() {
      const code = (this.newRegularsCode || '').trim();
      if (!/^\d{4}$/.test(code)) {
        this.gateCodesError = 'Code must be exactly 4 digits.';
        return;
      }
      this.gateCodesBusy = true;
      this.gateCodesError = '';
      try {
        const response = await fetch(this.gateCodesAdminUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'set_code', role: 'regulars', code })
        });
        const data = await response.json();
        if (data.success) {
          this.newRegularsCode = '';
          await this.loadGateCodes();
          this.result = 'Regulars code updated. It works at the gate and on the DJ site.';
          this.success = true;
        } else {
          // The server refuses a code already used by the manager PIN, the guest code
          // or the DJ's daily code — surface that reason as-is.
          this.gateCodesError = data.error || 'Failed to set code.';
        }
      } catch (error) {
        this.gateCodesError = `Failed to set code: ${error.message}`;
      } finally {
        this.gateCodesBusy = false;
      }
    },

    async regenerateGuestCode() {
      this.gateCodesBusy = true;
      this.gateCodesError = '';
      try {
        const response = await fetch(this.gateCodesAdminUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'regenerate_guest' })
        });
        const data = await response.json();
        if (data.success) {
          await this.loadGateCodes();
          this.result = `New guest code: ${data.code}`;
          this.success = true;
        } else {
          this.gateCodesError = data.error || 'Failed to regenerate guest code.';
        }
      } catch (error) {
        this.gateCodesError = `Failed to regenerate: ${error.message}`;
      } finally {
        this.gateCodesBusy = false;
      }
    },

    formatCodeExpiry(ts) {
      try {
        return new Date(ts).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
      } catch {
        return ts;
      }
    },
    formatCodeDate(ts) {
      try {
        return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch {
        return ts;
      }
    },

    // Show history page - load initial data first
    async showHistory() {
      this.isLoading = true;

      try {
        // Set default dates first
        await this.setDefaultDates();
        // Load the history data for today
        await this.loadHistoryData();
        // Only switch to history page if everything loaded successfully
        this.pageSwitch(3);
      } catch (error) {
        this.result = `Failed to load history page: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Show create user page (placeholder for now)
    async showCreateUser() {
      this.isLoading = true;
      
      try {
        // Clear any previous selection when entering user management
        this.selectedUser = null;
        // Load active users by default when opening the page
        this.userFilter = 'active';
        await this.loadUsersData();
        // Only switch to users page if everything loaded successfully
        this.pageSwitch(4);
      } catch (error) {
        this.result = `Failed to load users page: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Go back to gate control page
    backToGateControl() {
      this.pageSwitch(2);
    },

    // Set default dates (today for both start and end)
    async setDefaultDates() {
      if (!this.getTodayUrl) {
        throw new Error('Get Today URL not configured');
      }

      try {
        // Get today's date from the server
        const response = await fetch(this.getTodayUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const serverDate = new Date(data.server_time);
        // YYYY-MM-DD in the browser’s local timezone
        const dateString = serverDate.toLocaleDateString('en-CA');
        this.startDate = dateString;
        this.endDate = dateString;

      } catch (error) {
        // Fallback to client date
        const today = new Date().toISOString().split('T')[0];
        this.startDate = today;
        this.endDate = today;
        // Re-throw the error so showHistory can handle it
        throw error;
      }
    },

    // Load history data (called by Load History button)
    async loadHistory() {
      this.isLoading = true;

      try {
        await this.loadHistoryData();
      } catch (error) {
        this.result = `Failed to load history: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Internal method to load history data
    async loadHistoryData() {
      if (!this.startDate || !this.endDate) {
        throw new Error('Please select both start and end dates');
      }

      if (new Date(this.startDate) > new Date(this.endDate)) {
        throw new Error('Start date cannot be after end date');
      }

      if (!this.getHistoryUrl) {
        throw new Error('History API URL not configured');
      }

      this.historyData = [];
      this.historyLoaded = false;

      try {
        // Convert dates to ISO format for API, as local time
        const startLocal = new Date(this.startDate + 'T00:00:00');
        const endLocal = new Date(this.endDate + 'T23:59:59.999');
        const startTime = startLocal.toISOString();
        const endTime = endLocal.toISOString();


        const response = await fetch(this.getHistoryUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY
          },
          body: JSON.stringify({
            uuid: this.accessToken,
            start_time: startTime,
            end_time: endTime
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Check if the request was authorized and we have history data
        if (data.authorized && data.history && Array.isArray(data.history)) {
          // Format the history data for display, compute durations from on/off pairs
          this.historyData = data.history.map((item, index, arr) => {
            let duration = '';
            // If this is a gate close event, find the matching open event
            if (item.gate_state === 'off') {
              const onEvent = arr.slice(index + 1).find(e => e.gate_state === 'on');
              if (onEvent) {
                const diffSeconds = Math.round(
                  (new Date(item.event_time) - new Date(onEvent.event_time)) / 1000
                );
                const minutes = Math.floor(diffSeconds / 60);
                const seconds = diffSeconds % 60;
                duration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
              }
            }
            return {
              event_time: new Date(item.event_time).toLocaleString(),
              details: item.details || 'N/A',
              duration
            };
          });
          this.historyLoaded = true;
        } else if (data.authorized === false) {
          throw new Error('Authorization failed for history request');
        } else {
          throw new Error('Invalid response format: no history data found');
        }

      } catch (error) {
        this.historyLoaded = true; // Set to true so "no data" message can show
        throw error; // Re-throw so calling function can handle it
      }
    },

    // Set user filter and load users
    async setUserFilter(filter) {
      this.userFilter = filter;
      this.isLoading = true;

      try {
        await this.loadUsersData();
      } catch (error) {
        this.result = `Failed to load users: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Internal method to load users data
    async loadUsersData() {
      if (!this.getUsersUrl) {
        throw new Error('Users API URL not configured');
      }

      this.usersData = [];
      this.usersLoaded = false;
      this.selectedUser = null; // Clear selection when loading new data

      try {
        // Prepare request body based on filter - always include UUID
        let requestBody = { uuid: this.accessToken };
        if (this.userFilter === 'active') {
          requestBody.isvalid = true;
        } else if (this.userFilter === 'expired') {
          requestBody.isvalid = false;
        }
        // For 'all', only send UUID (no isvalid parameter)

        const response = await fetch(this.getUsersUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Check if the request was authorized and we have users data
        if (data.authorized && data.users && Array.isArray(data.users)) {
          // Format the users data for display and store original data
          this.usersData = data.users.map((user, index) => ({
            id: index, // Add an index for selection
            user_id: user.id, // Store the actual user ID
            name: user.name || 'N/A',
            phone: user.phone || 'N/A',
            expires_at: user.expires_at ? new Date(user.expires_at).toLocaleString() : 'N/A',
            expires_at_raw: user.expires_at, // Store raw date for editing
            created_at: user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A',
            is_manager: user.is_manager ? 'Yes' : 'No',
            is_manager_raw: user.is_manager, // Store raw boolean
            auto_renew: user.auto_renew ? 'Yes' : 'No',
            auto_renew_raw: user.auto_renew, // Store raw boolean
            status: this.getUserStatus(user.expires_at)
          }));
          this.usersLoaded = true;
        } else if (data.authorized === false) {
          throw new Error('Authorization failed for users request');
        } else {
          throw new Error('Invalid response format: no users data found');
        }

      } catch (error) {
        this.usersLoaded = true; // Set to true so "no data" message can show
        throw error; // Re-throw so calling function can handle it
      }
    },

    // Helper function to determine user status
    getUserStatus(expiresAt) {
      if (!expiresAt) return 'Unknown';
      const now = new Date();
      const expiry = new Date(expiresAt);
      return expiry > now ? 'Active' : 'Expired';
    },

    // Show add user page
    async showAddUser() {
      // Reset form data
      this.newUser = {
        name: '',
        phone: '',
        expires_at: '',
        is_manager: false,
        auto_renew: false,
        group_id: null
      };
      // Load available groups for the dropdown
      if (this.gateGroupsUrl) {
        try {
          const response = await fetch(this.gateGroupsUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
            body: JSON.stringify({ uuid: this.accessToken, action: 'list' })
          });
          const data = await response.json();
          if (data.success) {
            this.availableGroups = data.groups || [];
          }
        } catch {
          // Non-critical, just won't show groups dropdown
        }
      }
      this.pageSwitch(5);
    },

    // Handle user selection from table
    onUserSelect(selection) {
      // Handle both selection and deselection
      if (selection && selection.item && selection.selected) {
        this.selectedUser = selection.item;
      } else {
        this.selectedUser = null;
      }
    },

    // Show edit user overlay
    showEditUser() {
      if (!this.selectedUser) return;

      // Populate edit form with selected user data
      this.editUser = {
        user_id: this.selectedUser.user_id,
        name: this.selectedUser.name === 'N/A' ? '' : this.selectedUser.name,
        phone: this.selectedUser.phone === 'N/A' ? '' : this.selectedUser.phone,
        expires_at: '', // Will be set only if change_expires_at is checked
        is_manager: this.selectedUser.is_manager_raw || false,
        auto_renew: this.selectedUser.auto_renew_raw || false,
        new_uuid: false,
        change_expires_at: false
      };

      this.showEditOverlay = true;
    },

    // Close edit overlay
    closeEditOverlay() {
      this.showEditOverlay = false;
    },

    // Resend access link to selected user
    async resendLink() {
      if (!this.selectedUser) {
        this.result = 'No user selected';
        this.error = true;
        return;
      }

      if (!this.resendLinkUrl) {
        this.result = 'Resend link API URL not configured';
        this.error = true;
        return;
      }

      this.isLoading = true;

      try {
        const response = await fetch(this.resendLinkUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY
          },
          body: JSON.stringify({
            auth_uuid: this.accessToken,
            user_id: this.selectedUser.user_id
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          this.success = true;
          // Auto-close success overlay after 3 seconds
          setTimeout(() => {
            this.success = false;
          }, 3000);
        } else {
          throw new Error(data.sms_error || data.error || 'Failed to resend link');
        }

      } catch (error) {
        this.result = `Failed to resend link: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Format phone number for edit form
    formatEditPhoneNumber() {
      this.editUser.phone = this.formatPhone(this.editUser.phone);
    },

    // Update user
    async updateUser() {
      if (!this.isEditFormValid) {
        this.result = 'Please fill in all required fields correctly';
        this.error = true;
        return;
      }

      if (!this.editUserUrl) {
        this.result = 'Edit user API URL not configured';
        this.error = true;
        return;
      }

      this.isLoading = true;

      try {
        const requestBody = {
          uuid: this.accessToken,
          user_id: this.editUser.user_id,
          name: this.editUser.name.trim(),
          phone: this.editUser.phone.trim(),
          is_manager: this.editUser.is_manager,
          auto_renew: this.editUser.auto_renew,
          new_uuid: this.editUser.new_uuid
        };

        // Only include expires_at if the checkbox is checked
        if (this.editUser.change_expires_at && this.editUser.expires_at) {
          requestBody.expires_at = new Date(this.editUser.expires_at).toISOString();
        }

        const response = await fetch(this.editUserUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          this.success = true;
          this.closeEditOverlay();

          // Refresh users list after successful update
          setTimeout(() => {
            this.success = false;
            this.loadUsersData().catch(() => {
              this.result = 'Failed to refresh user list';
              this.error = true;
            });
          }, 2000);
        } else {
          throw new Error(data.error || 'Failed to update user');
        }

      } catch (error) {
        this.result = `Failed to update user: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Go back to user management page
    backToUserManagement() {
      this.pageSwitch(4);
    },

    // Format phone string to E.164 format
    formatPhone(phone) {
      phone = phone.trim();
      if (!phone) return phone;

      // Remove all non-digit characters except + — this also strips any
      // accidental HTML chars without needing a separate escape step.
      phone = phone.replace(/[^\d+]/g, '');

      // If it doesn't start with +, add +1 (assuming US/Canada)
      if (!phone.startsWith('+')) {
        if (phone.length === 10) {
          phone = '+1' + phone;
        } else if (phone.length === 11 && phone.startsWith('1')) {
          phone = '+' + phone;
        } else {
          // For other countries, user needs to include country code
          phone = '+' + phone;
        }
      }

      return phone;
    },

    // Format phone number for new user form
    formatPhoneNumber() {
      this.newUser.phone = this.formatPhone(this.newUser.phone);
    },

    // Validate phone number format
    isValidPhoneNumber(phone) {
      if (!phone) return false;
      // Basic E.164 format validation: starts with + and has 7-15 digits
      const e164Regex = /^\+[1-9]\d{6,14}$/;
      return e164Regex.test(phone.replace(/\s/g, ''));
    },

    // Create new user
    async createUser() {
      if (!this.isFormValid) {
        this.result = 'Please fill in all required fields correctly';
        this.error = true;
        return;
      }

      if (!this.createUserUrl) {
        this.result = 'Create user API URL not configured';
        this.error = true;
        return;
      }

      this.isLoading = true;

      try {
        // Format expires_at to ISO string
        const expiresAt = new Date(this.newUser.expires_at).toISOString();

        const response = await fetch(this.createUserUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY
          },
          body: JSON.stringify({
            auth_uuid: this.accessToken,
            name: this.newUser.name.trim(),
            phone: this.newUser.phone.trim(),
            expires_at: expiresAt,
            is_manager: this.newUser.is_manager,
            auto_renew: this.newUser.auto_renew
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          // Add to group if selected
          if (this.newUser.group_id && data.user && data.user.id && this.gateGroupsUrl) {
            try {
              await fetch(this.gateGroupsUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
                body: JSON.stringify({
                  uuid: this.accessToken,
                  action: 'add_member',
                  group_id: this.newUser.group_id,
                  user_id: data.user.id
                })
              });
            } catch {
              // Non-critical — user was created, group add failed silently
            }
          }
          this.success = true;
          // Go back to user management and refresh the list
          setTimeout(() => {
            this.success = false;
            this.backToUserManagement();
            // Refresh users list
            this.loadUsersData().catch(() => {
              this.result = 'Failed to refresh user list';
              this.error = true;
            });
          }, 2000);
        } else if (data.warning) {
          // User created but SMS failed
          this.result = data.warning;
          this.error = true;
          // Still go back and refresh after user acknowledges
          setTimeout(() => {
            this.backToUserManagement();
            this.loadUsersData().catch(() => {
              this.result = 'Failed to refresh user list';
              this.error = true;
            });
          }, 1000);
        } else {
          throw new Error(data.error || 'Failed to create user');
        }

      } catch (error) {
        this.result = `Failed to create user: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Issue new link for a single user
    openIssueNewLinkOverlay() {
      if (!this.selectedUser) return;
      this.issueNewLinkExpiration = '';
      this.showIssueNewLinkOverlay = true;
    },

    async issueNewLink() {
      if (!this.selectedUser || !this.issueNewLinkExpiration) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.editUserUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({
            uuid: this.accessToken,
            user_id: this.selectedUser.user_id,
            name: this.selectedUser.name,
            phone: this.selectedUser.phone,
            is_manager: this.selectedUser.is_manager_raw,
            auto_renew: this.selectedUser.auto_renew_raw,
            new_uuid: true,
            expires_at: new Date(this.issueNewLinkExpiration).toISOString()
          })
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.success) {
          this.showIssueNewLinkOverlay = false;
          this.success = true;
          setTimeout(() => {
            this.success = false;
            this.loadUsersData().catch(() => {});
          }, 2000);
        } else {
          throw new Error(data.error || 'Failed to issue new link');
        }
      } catch (error) {
        this.result = `Failed to issue new link: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Manage user groups from page 4
    async openManageUserGroups() {
      if (!this.selectedUser) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.gateGroupsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'get_user_groups', user_id: this.selectedUser.user_id })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to load groups');
        this.userGroups = data.user_groups || [];
        this.userAvailableGroups = data.available_groups || [];
        this.showManageUserGroupsOverlay = true;
      } catch (error) {
        this.result = `Failed to load groups: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    async addUserToGroup(groupId) {
      if (!this.selectedUser) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.gateGroupsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'add_member', group_id: groupId, user_id: this.selectedUser.user_id })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to add to group');
        await this.openManageUserGroups();
      } catch (error) {
        this.result = `Failed to add to group: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    async removeUserFromGroup(groupId) {
      if (!this.selectedUser) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.gateGroupsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'remove_member', group_id: groupId, user_id: this.selectedUser.user_id })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to remove from group');
        await this.openManageUserGroups();
      } catch (error) {
        this.result = `Failed to remove from group: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // --- Groups Methods ---

    // Show groups list page
    async showGroups() {
      this.isLoading = true;
      try {
        this.selectedGroup = null;
        await this.loadGroupsData();
        this.pageSwitch(6);
      } catch (error) {
        this.result = `Failed to load groups: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Load groups from API
    async loadGroupsData() {
      if (!this.gateGroupsUrl) throw new Error('Groups API URL not configured');

      const response = await fetch(this.gateGroupsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
        body: JSON.stringify({ uuid: this.accessToken, action: 'list' })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to load groups');

      this.groupsData = (data.groups || []).map((g, i) => ({
        id: i,
        group_id: g.id,
        name: g.name,
        member_count: g.member_count || 0,
        created_at: new Date(g.created_at).toLocaleDateString()
      }));
      this.groupsLoaded = true;
    },

    // Handle group row selection
    onGroupSelect(selection) {
      if (selection && selection.item && selection.selected) {
        this.selectedGroup = selection.item;
      } else {
        this.selectedGroup = null;
      }
    },

    // Create a new group
    async createGroup() {
      if (!this.newGroupName.trim()) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.gateGroupsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'create', name: this.newGroupName.trim() })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to create group');

        this.showCreateGroupOverlay = false;
        this.success = true;
        setTimeout(() => { this.success = false; }, 2000);
        await this.loadGroupsData();
      } catch (error) {
        this.result = `Failed to create group: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Confirm delete group
    confirmDeleteGroup() {
      if (!this.selectedGroup) return;
      this.groupToDelete = this.selectedGroup;
      this.showDeleteGroupConfirm = true;
    },

    // Delete a group
    async deleteGroup() {
      if (!this.groupToDelete) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.gateGroupsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken, action: 'delete', group_id: this.groupToDelete.group_id })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to delete group');

        this.showDeleteGroupConfirm = false;
        this.groupToDelete = null;
        this.selectedGroup = null;
        this.success = true;
        setTimeout(() => { this.success = false; }, 2000);
        await this.loadGroupsData();
      } catch (error) {
        this.result = `Failed to delete group: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // View group detail
    async viewGroupDetail() {
      if (!this.selectedGroup) return;
      this.isLoading = true;
      try {
        this.currentGroup = this.selectedGroup;
        this.selectedMemberInGroup = null;
        await this.loadGroupMembers();
        this.pageSwitch(7);
      } catch (error) {
        this.result = `Failed to load group: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Load group members
    async loadGroupMembers() {
      if (!this.currentGroup) return;

      const response = await fetch(this.gateGroupsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
        body: JSON.stringify({ uuid: this.accessToken, action: 'get_members', group_id: this.currentGroup.group_id })
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to load members');

      this.groupMembers = (data.members || []).map((m, i) => ({
        id: i,
        user_id: m.user_id,
        name: m.name,
        phone: m.phone,
        expires_at: m.expires_at ? new Date(m.expires_at).toLocaleString() : 'N/A',
        status: this.getUserStatus(m.expires_at)
      }));
      this.groupMembersLoaded = true;
    },

    // Handle group member row selection
    onGroupMemberSelect(selection) {
      if (selection && selection.item && selection.selected) {
        this.selectedMemberInGroup = selection.item;
      } else {
        this.selectedMemberInGroup = null;
      }
    },

    // Back to groups list
    backToGroups() {
      this.selectedGroup = null;
      this.loadGroupsData().catch(() => {});
      this.pageSwitch(6);
    },

    // Open add member overlay - load all users not already in group
    async openAddMemberOverlay() {
      this.isLoading = true;
      try {
        // Load all users
        const response = await fetch(this.getUsersUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({ uuid: this.accessToken })
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (!data.authorized || !data.users) throw new Error('Failed to load users');

        // Filter out users already in the group
        const memberIds = new Set(this.groupMembers.map(m => m.user_id));
        this.allUsersForGroup = data.users
          .filter(u => !memberIds.has(u.id))
          .map((u, i) => ({
            id: i,
            user_id: u.id,
            name: u.name || 'N/A',
            phone: u.phone || 'N/A',
            status: this.getUserStatus(u.expires_at)
          }));

        this.selectedMemberToAdd = null;
        this.showAddMemberOverlay = true;
      } catch (error) {
        this.result = `Failed to load users: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Handle add member selection
    onAddMemberSelect(selection) {
      if (selection && selection.item && selection.selected) {
        this.selectedMemberToAdd = selection.item;
      } else {
        this.selectedMemberToAdd = null;
      }
    },

    // Add member to group
    async addMemberToGroup() {
      if (!this.selectedMemberToAdd || !this.currentGroup) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.gateGroupsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({
            uuid: this.accessToken,
            action: 'add_member',
            group_id: this.currentGroup.group_id,
            user_id: this.selectedMemberToAdd.user_id
          })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to add member');

        this.showAddMemberOverlay = false;
        this.success = true;
        setTimeout(() => { this.success = false; }, 2000);
        await this.loadGroupMembers();
      } catch (error) {
        this.result = `Failed to add member: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Remove member from group
    async removeMemberFromGroup() {
      if (!this.selectedMemberInGroup || !this.currentGroup) return;
      this.isLoading = true;
      try {
        const response = await fetch(this.gateGroupsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({
            uuid: this.accessToken,
            action: 'remove_member',
            group_id: this.currentGroup.group_id,
            user_id: this.selectedMemberInGroup.user_id
          })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to remove member');

        this.selectedMemberInGroup = null;
        this.success = true;
        setTimeout(() => { this.success = false; }, 2000);
        await this.loadGroupMembers();
      } catch (error) {
        this.result = `Failed to remove member: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Open send links overlay
    openSendLinksOverlay() {
      this.sendLinksExpiration = '';
      this.sendLinksResults = null;
      this.sendLinksInProgress = false;
      this.sendLinksNewLinks = false;
      this.showSendLinksOverlay = true;
    },

    // Close send links overlay
    closeSendLinksOverlay() {
      this.showSendLinksOverlay = false;
      this.sendLinksResults = null;
      // Refresh members to show updated expirations
      if (this.currentGroup) {
        this.loadGroupMembers().catch(() => {});
      }
    },

    // Send links to all group members
    async sendLinksToGroup() {
      if (!this.currentGroup || !this.sendLinksExpiration) return;
      this.sendLinksInProgress = true;
      this.isLoading = true;
      try {
        const expiresAt = new Date(this.sendLinksExpiration).toISOString();
        const response = await fetch(this.gateGroupsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.VUE_APP_SUPABASE_KEY },
          body: JSON.stringify({
            uuid: this.accessToken,
            action: 'send_links',
            group_id: this.currentGroup.group_id,
            expires_at: expiresAt,
            new_links: this.sendLinksNewLinks
          })
        });
        const data = await response.json();
        if (!data.success) throw new Error(data.error || 'Failed to send links');

        this.sendLinksResults = data;
      } catch (error) {
        this.result = `Failed to send links: ${error.message}`;
        this.error = true;
      } finally {
        this.isLoading = false;
        this.sendLinksInProgress = false;
      }
    },

    // Page switching function
    pageSwitch(page) {
      const pageMap = {
        1: ['page1'], // Auth failure
        2: ['page2'], // Gate control
        3: ['page3'], // History
        4: ['page4'], // Users
        5: ['page5'], // Add user
        6: ['page6'], // Groups list
        7: ['page7'], // Group detail
        8: ['page8']  // Gate codes
      };

      // Reset all flags to false
      this.page1 = false;
      this.page2 = false;
      this.page3 = false;
      this.page4 = false;
      this.page5 = false;
      this.page6 = false;
      this.page7 = false;
      this.page8 = false;

      // Set only the relevant flags to true
      if (pageMap[page]) {
        pageMap[page].forEach(flag => this[flag] = true);
      }
    }
  }
};


</script>

<style>
/* === Global Font Application === */
.w-app {
  font-family: var(--font-body);
  padding: 4px;
  background-color: var(--color-cream);
  color: var(--color-charcoal);
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-heading);
  color: var(--color-charcoal);
}

/* === Authentication Failure === */
.auth-failure-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--color-parchment);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--color-charcoal);
  z-index: 10000;
}

.auth-failure-main {
  background: var(--color-parchment) !important;
  border: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* === Keypad code-entry screen === */
.keypad-screen {
  max-width: 340px;
  margin: 0 auto;
  padding: var(--space-lg) var(--space-md) var(--space-xl);
}
.keypad-subtitle {
  color: var(--color-text-muted);
  font-size: 16px;
  margin: var(--space-xs) 0 0;
}
.keypad-hint {
  color: var(--color-text-muted);
  font-size: 12px;
  margin: var(--space-xs) auto 0;
  max-width: 280px;
  text-align: center;
  opacity: 0.85;
}
.keypad-error {
  color: #b00020;
  font-size: 15px;
  margin: var(--space-sm) 0 0;
  text-align: center;
  max-width: 300px;
}
.code-dots {
  display: flex;
  gap: var(--space-md);
  margin: var(--space-lg) 0 var(--space-sm);
}
.code-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--color-rope);
  background: transparent;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}
.code-dot.filled {
  background: var(--color-copper);
  border-color: var(--color-copper);
}
.keypad-grid {
  display: grid;
  grid-template-columns: repeat(3, 84px);
  gap: var(--space-sm);
  margin-top: var(--space-md);
  justify-content: center;
}
.keypad-grid .keypad-key.w-button {
  width: 84px;
  height: 64px;
  font-size: 26px;
  font-weight: 600;
}
.keypad-grid .keypad-key-muted.w-button {
  font-size: 18px;
  font-weight: 500;
}
.cf-turnstile-container {
  min-height: 0;
}

/* === Gate Codes panel === */
.codes-panel {
  max-width: 420px;
  margin: var(--space-md) auto 0;
  padding: 0 var(--space-md) var(--space-xl);
}
.code-card {
  background: var(--color-parchment);
  border: var(--border-subtle);
  border-radius: var(--radius-md);
  padding: var(--space-md);
  margin-bottom: var(--space-md);
}
.code-card-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-md);
}
.code-role {
  font-family: var(--font-heading);
  font-size: 16px;
  color: var(--color-charcoal);
}
.code-value {
  font-family: var(--font-body);
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 4px;
  color: var(--color-copper);
}
/* Hashed codes (manager, regulars) have no read-back — masked, not missing. */
.code-value-hidden {
  color: var(--color-rope);
  letter-spacing: 6px;
}
.code-hint {
  color: var(--color-text-muted);
  font-size: 13px;
  margin: var(--space-xs) 0 var(--space-sm);
}
.code-set-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}
.code-input {
  width: 130px;
  font-size: 20px !important;
  letter-spacing: 3px;
  text-align: center;
}

/* === Input Styles === */
input[type="text"],
input[type="number"],
input[type="password"],
input[type="tel"],
input[type="date"],
input[type="datetime-local"] {
  font-family: var(--font-body);
  font-size: 24px;
  padding: var(--space-sm) var(--space-md);
  border: var(--border-subtle);
  border-radius: var(--radius-sm);
  background-color: var(--color-parchment);
  color: var(--color-charcoal);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

input[type="text"]:focus,
input[type="number"]:focus,
input[type="password"]:focus,
input[type="tel"]:focus,
input[type="date"]:focus,
input[type="datetime-local"]:focus {
  outline: none;
  border-color: var(--color-copper);
  box-shadow: 0 0 0 3px rgba(212, 118, 10, 0.2);
}

input[type="number"] {
  width: 195px;
}

/* === Layout Utilities === */
.stream {
  max-height: 300px;
  max-width: 300px;
  margin: auto;
}

.center-screen {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.right-screen {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  text-align: right;
}

.left-screen {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  text-align: left;
}

.space {
  width: 150px;
  height: auto;
  display: inline-block;
}

.mediumspace {
  width: 75px;
  height: auto;
  display: inline-block;
}

.smallspace {
  width: 35px;
  height: auto;
  display: inline-block;
}

.supersmallspace {
  width: 10px;
  height: auto;
  display: inline-block;
}

.disable-dbl-tap-zoom {
  touch-action: manipulation;
}

/* === Loading Spinner === */
.loading-spinner {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  background-color: var(--color-parchment);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  box-shadow: var(--shadow-lg);
}

/* === Header / Footer / Main === */
header,
footer,
aside,
main {
  margin: 4px;
  padding: 12px;
}

header {
  background-color: var(--color-walnut);
  min-height: 150px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
}

header h1, header h2, header h3, header p {
  color: var(--color-text-light);
}

footer {
  background-color: var(--color-walnut);
  color: var(--color-text-light);
  min-height: 60px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
}

footer h1, footer h2, footer h3, footer p {
  color: var(--color-text-light);
}

main {
  background-color: var(--color-cream-dark);
  border-radius: var(--radius-md);
}

/* === Button Overrides === */
.w-button {
  border-radius: var(--radius-sm) !important;
  font-family: var(--font-body) !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
  transition: transform var(--transition-fast), box-shadow var(--transition-fast) !important;
}

.w-button:active {
  transform: scale(0.97) !important;
}

.w-button.w-button--shadow {
  box-shadow: var(--shadow-button) !important;
}

/* === Button Group === */
.button-group {
  display: flex;
  gap: var(--space-md);
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

/* === Gate Status Text === */
.gate-status-text {
  color: var(--color-text-muted);
  margin: var(--space-xs) 0;
}

.gate-mode-text {
  color: var(--color-copper);
  font-weight: bold;
  margin: var(--space-xs) 0;
}

.gate-lockout-text {
  color: var(--color-text-muted);
  font-style: italic;
  margin-top: var(--space-sm);
}

/* === History Screen === */
.date-selector-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--space-lg);
}

.date-input-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.date-input-group label {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: var(--space-xs);
  color: var(--color-charcoal);
}

.date-input {
  padding: var(--space-sm) var(--space-md);
  font-size: 16px;
  font-family: var(--font-body);
  border: var(--border-subtle);
  border-radius: var(--radius-sm);
  background-color: var(--color-parchment);
  color: var(--color-charcoal);
  min-width: 150px;
}

.date-input:focus {
  outline: none;
  border-color: var(--color-copper);
  box-shadow: 0 0 0 3px rgba(212, 118, 10, 0.2);
}

.history-table-container {
  margin: var(--space-lg);
  padding: var(--space-lg);
  background-color: var(--color-parchment);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
}

/* === User Filter === */
.user-filter-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--space-sm);
}

/* === Table Overrides === */
.w-table__header th {
  background-color: var(--color-walnut) !important;
  color: var(--color-text-light) !important;
  font-family: var(--font-body);
  font-weight: 600;
}

.w-table__row:nth-child(even) {
  background-color: var(--color-cream-dark);
}

.w-table__row:nth-child(odd) {
  background-color: var(--color-parchment);
}

/* === Form Styling === */
.form-container {
  max-width: 500px;
  margin: 0 auto;
  padding: var(--space-lg);
  background-color: var(--color-parchment);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
}

.form-field {
  margin-bottom: var(--space-lg);
}

.form-field label {
  display: block;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: var(--space-xs);
  color: var(--color-charcoal);
}

.form-input {
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  font-size: 16px;
  font-family: var(--font-body);
  border: var(--border-subtle);
  border-radius: var(--radius-sm);
  background-color: var(--color-parchment);
  color: var(--color-charcoal);
  box-sizing: border-box;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-copper);
  box-shadow: 0 0 0 3px rgba(212, 118, 10, 0.2);
}

.form-input:invalid {
  border-color: #C62828;
}

.checkbox-field {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.checkbox-field label {
  margin-bottom: 0;
  cursor: pointer;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Revocation-gotcha warning shown in the edit-user overlay when the
   operator is about to set expires_at past while auto_renew is still on. */
.auto-renew-warning {
  margin-top: var(--space-md);
  padding: var(--space-md);
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: var(--radius-sm);
  color: #856404;
  font-size: 0.9em;
  line-height: 1.4;
}

/* === Overlay / Edit User Styling === */
.edit-user-container {
  background-color: var(--color-parchment);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: 600px;
  width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.edit-user-header {
  background-color: var(--color-walnut);
  border-bottom: var(--border-copper);
  padding: var(--space-lg);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  text-align: center;
}

.edit-user-header h2 {
  margin: 0;
  color: var(--color-text-light);
  font-size: 24px;
  font-family: var(--font-heading);
}

.edit-user-container .form-container {
  margin: 0;
  border-radius: 0;
  box-shadow: none;
  border: none;
}

.edit-user-buttons {
  padding: var(--space-lg);
  border-top: var(--border-subtle);
  background-color: var(--color-cream-dark);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
  display: flex;
  justify-content: center;
  gap: var(--space-md);
}

/* === Overlay Cards (Groups, Settings, etc.) === */
.overlay-card {
  background-color: var(--color-parchment);
  border-radius: var(--radius-lg);
  padding: var(--space-xl);
  box-shadow: var(--shadow-lg);
  text-align: center;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
}

.overlay-card h1, .overlay-card h2, .overlay-card h3 {
  font-family: var(--font-heading);
  color: var(--color-charcoal);
}

.overlay-card h2 {
  border-bottom: var(--border-copper);
  padding-bottom: var(--space-sm);
  margin-bottom: var(--space-md);
}

.overlay-card label {
  font-family: var(--font-body);
  font-size: 1.1rem;
  color: var(--color-text-muted);
  display: block;
  margin-bottom: var(--space-xs);
}

/* === Send Links Results === */
.send-results-text {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-charcoal);
}

.send-results-success {
  color: var(--color-copper);
}

.send-results-fail {
  color: #C62828;
}

/* === Settings Help Text === */
.settings-help {
  font-size: 12px;
  color: var(--color-text-muted);
  margin-top: var(--space-xs);
}

/* === Manage Groups Lists === */
.manage-groups-section h3 {
  font-family: var(--font-heading);
  color: var(--color-charcoal);
  margin-bottom: var(--space-sm);
}

.manage-groups-empty {
  color: var(--color-text-muted);
  font-style: italic;
}
</style>
